import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import MainModel from "../../../../components/Models/main-model";
import MainInput from "../../../../components/Form/input";
import {
  adminModalBodyProps,
  adminModalContentProps,
  adminModalHeaderProps,
} from "../../../../components/Models/admin-modal-theme";
import { closeMarkSchemeModel } from "../../../../modules/admin-modules/models/Actions";
import { createMarkSchemeRequest } from "../../../../modules/admin-modules/mark-schemes/Actions";
import { brandPalette } from "../../../../constants/brandPalette";

const getPdfFile = (value) => {
  if (value instanceof File) return value;
  if (value instanceof FileList && value.length > 0) return value[0];
  return null;
};

const MAX_PDF_SIZE_BYTES = 50 * 1024 * 1024;

const formatFileSize = (bytes) => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.ceil(bytes / 1024)} KB`;
};

const AddMarkScheme = () => {
  const dispatch = useDispatch();
  const open = useSelector((state) => state.model.isOpen44);
  const isLoading = useSelector((state) => state.markSchemes.isLoading);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: {},
    shouldUnregister: true,
  });

  const fileField = register("url");

  const header = (
    <Box>
      <Heading
        fontSize="32px"
        fontWeight="800"
        color="var(--text-primary)"
        fontFamily="'Amiri','Tajawal',sans-serif"
      >
        Add Mark Scheme
      </Heading>
      <Text
        fontSize="xs"
        mt={1}
        color="var(--text-muted)"
        textTransform="uppercase"
        letterSpacing="0.14em"
        fontWeight="700"
      >
        Upload a PDF mark scheme for your subject
      </Text>
    </Box>
  );

  const closeFunc = () => {
    dispatch(closeMarkSchemeModel());
    reset();
    setSelectedFile(null);
    setSelectedFileName("");
  };

  const onSubmit = async (values) => {
    const file = getPdfFile(selectedFile) ?? getPdfFile(values.url);
    if (!file) {
      toast.error("Please select a PDF file.");
      return;
    }
    if (file.size > MAX_PDF_SIZE_BYTES) {
      toast.error(
        `PDF is too large (${formatFileSize(file.size)}). Maximum allowed size is ${formatFileSize(MAX_PDF_SIZE_BYTES)}.`
      );
      return;
    }

    const formData = new FormData();
    formData.append("title", values?.title ?? "");
    formData.append("url", file);

    await dispatch(createMarkSchemeRequest(formData, closeFunc));
  };

  return (
    <MainModel
      isLoading={isLoading}
      header={header}
      closeFunc={closeFunc}
      openVar={open}
      buttonTitle={null}
      buttonFunc={handleSubmit(onSubmit)}
      size="2xl"
      contentProps={adminModalContentProps}
      headerProps={adminModalHeaderProps}
      bodyProps={adminModalBodyProps}
      hideDefaultFooter
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={6} dir="ltr" w="100%">
          <MainInput
            label="Scheme title"
            error={errors?.title?.message}
            placeholder="e.g. June 2024 Paper 1 Mark Scheme"
            register={register}
            name="title"
            required
          />

          <FormControl>
            <FormLabel fontWeight="700" color="var(--text-primary)">
              PDF file
            </FormLabel>
            <VStack
              as="label"
              align="stretch"
              spacing={2}
              border="1px dashed"
              borderColor={brandPalette.stone}
              borderRadius="lg"
              px={4}
              py={6}
              cursor="pointer"
              bg="var(--surface-muted)"
              _hover={{ borderColor: brandPalette.primary }}
            >
              <Text fontWeight="700" color="var(--text-primary)">
                {selectedFileName || "Choose file to upload"}
              </Text>
              <Input
                {...fileField}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > MAX_PDF_SIZE_BYTES) {
                    toast.error(
                      `PDF is too large (${formatFileSize(file.size)}). Maximum allowed size is ${formatFileSize(MAX_PDF_SIZE_BYTES)}.`
                    );
                    e.target.value = "";
                    return;
                  }
                  fileField.onChange(e);
                  setValue("url", file, { shouldDirty: true });
                  setSelectedFileName(file.name);
                  setSelectedFile(file);
                }}
                type="file"
                accept="application/pdf,.pdf"
                required={false}
                display="none"
              />
              <Text fontSize="sm" color="var(--text-muted)">
                Maximum file size: {formatFileSize(MAX_PDF_SIZE_BYTES)} (PDF only)
              </Text>
            </VStack>
          </FormControl>

          <Flex justify="flex-end" gap={6} pt={4}>
            <Button
              variant="ghost"
              color="var(--text-primary)"
              fontWeight="800"
              _hover={{ textDecoration: "underline", bg: "rgba(91, 13, 22, 0.08)" }}
              onClick={closeFunc}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              bg={brandPalette.primary}
              color="white"
              borderRadius="lg"
              px={8}
              py={6}
              fontWeight="800"
              isLoading={isLoading}
              _hover={{ bg: brandPalette.accentDark }}
              boxShadow="0 10px 24px rgba(91, 13, 22, 0.18)"
            >
              Save
            </Button>
          </Flex>
        </Stack>
      </form>
    </MainModel>
  );
};

export default AddMarkScheme;
